/**
 * Assignment Form Component
 * For creating new assignment requests
 */

import React, { useState } from 'react';
import { useAuth } from '../../../core/contexts/AuthContext'; // Fixed import path
import { FileUp, HelpCircle, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';

const AssignmentForm = ({ onSubmit, isUploading }) => {
  const { userProfile } = useAuth();
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [subject, setSubject] = useState('');
  const [course, setCourse] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [budget, setBudget] = useState('');
  const [timeEstimate, setTimeEstimate] = useState('');
  const [showIntegrityPolicy, setShowIntegrityPolicy] = useState(false);
  const [acceptedPolicy, setAcceptedPolicy] = useState(false);

  // Detect user's university from profile
  const userUniversity = userProfile?.academic?.university || '';
  const userYearLevel = userProfile?.academic?.yearOfStudy || '';
  
  // Handle file change
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    
    if (!selectedFile) return;
    
    // Check file size (max 10MB)
    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error('File size should be less than 10MB');
      return;
    }
    
    // Check file type
    const validTypes = [
      'application/pdf', 
      'application/msword', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain',
      'image/jpeg',
      'image/png'
    ];
    
    if (!validTypes.includes(selectedFile.type)) {
      toast.error('Invalid file type. Please upload PDF, Word, PowerPoint, Excel, text, or image files.');
      return;
    }
    
    setFile(selectedFile);
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!file) {
      toast.error('Please upload an assignment file');
      return;
    }
    
    if (!acceptedPolicy) {
      toast.error('Please accept the academic integrity policy');
      return;
    }
    
    // Format due date as Date object
    const formattedDueDate = dueDate ? new Date(dueDate) : null;
    
    const assignmentData = {
      title,
      description,
      subject,
      course,
      dueDate: formattedDueDate,
      budget,
      timeEstimate,
      university: userUniversity,
      yearLevel: userYearLevel
    };
    
    onSubmit(assignmentData, file);
  };
  
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <h2 className="text-xl font-semibold text-gray-800 mb-4">Request Tutoring Help</h2>
      
      <form onSubmit={handleSubmit}>
        {/* Title */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Assignment Title*
          </label>
          <input
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            placeholder="e.g., Calculus 101 Homework"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </div>
        
        {/* Subject & Course */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subject*
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="e.g., Mathematics"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Course*
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="e.g., MATH101"
              value={course}
              onChange={(e) => setCourse(e.target.value)}
              required
            />
          </div>
        </div>
        
        {/* Description */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description*
          </label>
          <textarea
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 min-h-[100px]"
            placeholder="Describe what kind of help you need with this assignment..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
          />
        </div>
        
        {/* Due Date & Budget */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Due Date*
            </label>
            <input
              type="date"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]} // Minimum date is today
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Budget (ZAR)*
            </label>
            <input
              type="number"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="e.g., 200"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              min="50"
              required
            />
            <p className="text-xs text-gray-500 mt-1">Minimum budget: R50</p>
          </div>
        </div>
        
        {/* Time Estimate */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Estimated Time Required (hours)*
          </label>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            value={timeEstimate}
            onChange={(e) => setTimeEstimate(e.target.value)}
            required
          >
            <option value="">Select time estimate</option>
            <option value="0-1">Less than 1 hour</option>
            <option value="1-2">1-2 hours</option>
            <option value="2-4">2-4 hours</option>
            <option value="4-8">4-8 hours</option>
            <option value="8+">8+ hours</option>
          </select>
        </div>
        
        {/* File Upload */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Upload Assignment File*
          </label>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:bg-gray-50 transition cursor-pointer">
            <input
              type="file"
              id="assignment-file"
              className="hidden"
              onChange={handleFileChange}
              accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.jpg,.jpeg,.png"
            />
            <label htmlFor="assignment-file" className="cursor-pointer">
              {file ? (
                <div className="flex items-center justify-center">
                  <FileUp className="w-5 h-5 text-green-500 mr-2" />
                  <span className="text-green-500">{file.name}</span>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center">
                  <FileUp className="w-8 h-8 text-gray-400 mb-2" />
                  <p className="text-sm text-gray-500">
                    Click to browse or drag and drop
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Supports PDF, Word, PowerPoint, Excel, Text, and Images (max 10MB)
                  </p>
                </div>
              )}
            </label>
          </div>
        </div>
        
        {/* Academic Integrity Policy */}
        <div className="mb-6 bg-yellow-50 p-4 rounded-lg border border-yellow-100">
          <div className="flex items-start">
            <div className="mr-3 mt-0.5">
              <AlertCircle className="w-5 h-5 text-yellow-500" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-800">Academic Integrity Policy</h3>
              <p className="text-xs text-gray-600 mt-1">
                VarsityLink is committed to academic integrity. By requesting help, you agree to our policies.
                <button
                  type="button"
                  className="ml-1 text-green-500 hover:underline font-medium"
                  onClick={() => setShowIntegrityPolicy(!showIntegrityPolicy)}
                >
                  {showIntegrityPolicy ? 'Hide details' : 'View details'}
                </button>
              </p>
              
              {showIntegrityPolicy && (
                <div className="mt-3 text-xs text-gray-600 p-3 bg-white rounded border border-gray-200">
                  <p className="font-medium mb-2">VarsityLink Academic Integrity Policy:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Tutors provide guidance and assistance only, not completed work for submission.</li>
                    <li>Any work created during tutoring sessions should be used as a learning resource.</li>
                    <li>You remain responsible for your academic submissions and understanding of the material.</li>
                    <li>Submitting work created by others as your own constitutes plagiarism.</li>
                    <li>VarsityLink does not condone any activities that violate your institution's academic integrity policy.</li>
                    <li>Tutors are instructed to decline requests that appear to violate academic integrity.</li>
                  </ul>
                </div>
              )}
              
              <div className="mt-3">
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    className="mt-0.5 mr-2"
                    checked={acceptedPolicy}
                    onChange={(e) => setAcceptedPolicy(e.target.checked)}
                    required
                  />
                  <span className="text-xs">
                    I understand and agree that all assistance received through VarsityLink must comply with my institution's academic integrity policies. I will only use tutoring for learning purposes and not to circumvent academic requirements.
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>
        
        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            className="px-4 py-2 bg-gradient-to-r from-green-500 to-teal-500 text-white rounded-md hover:from-green-600 hover:to-teal-600 transition shadow-sm disabled:opacity-70 disabled:cursor-not-allowed"
            disabled={isUploading || !acceptedPolicy}
          >
            {isUploading ? (
              <>
                <span className="inline-block w-4 h-4 border-2 border-t-transparent border-white rounded-full animate-spin mr-2"></span>
                Uploading...
              </>
            ) : (
              'Post Assignment'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AssignmentForm;